import { Helmet } from 'react-helmet';
import {
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  TWITTER_CARD,
  TWITTER_SITE,
  OG_SITE_NAME,
  getFullTitle,
  getOrigin,
} from '@/config/seo';
import { toAbsoluteUrl } from '@/lib/utils';

interface SEOHeadProps {
  title: string;
  description: string;
  type?: 'website' | 'article' | 'profile';
  image?: string;
  url?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  schema?: object | object[];
  canonicalUrl?: string;
  hreflangLinks?: Array<{ lang: string; url: string }>;
  noindex?: boolean;
  /** OG locale tag, e.g. "pt_BR", "en_US" */
  locale?: string;
  /** Override twitter:card (default: summary_large_image). Use "summary" for square avatars. */
  twitterCardOverride?: 'summary' | 'summary_large_image';
  /** Profile OG: first name */
  profileFirstName?: string;
  /** Profile OG: last name */
  profileLastName?: string;
}

/**
 * SEOHead component for comprehensive SEO meta tags
 * Includes Open Graph, Twitter Cards, hreflang links, and Schema.org structured data
 */
export function SEOHead({
  title,
  description,
  type = 'website',
  image = DEFAULT_OG_IMAGE,
  url,
  author,
  publishedTime,
  modifiedTime,
  schema,
  canonicalUrl,
  hreflangLinks = [],
  noindex = false,
  locale,
  twitterCardOverride,
  profileFirstName,
  profileLastName,
}: SEOHeadProps) {
  const fullTitle = getFullTitle(title);
  const origin = getOrigin();
  const currentUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const imageUrl = toAbsoluteUrl(image) || `${origin}${DEFAULT_OG_IMAGE}`;
  const canonical = canonicalUrl || currentUrl;

  // Convert single schema to array for consistent handling
  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  // Determine twitter card type
  const twitterCard = twitterCardOverride || TWITTER_CARD;

  // Check if x-default already exists in hreflangLinks to avoid duplicates
  const hasXDefault = hreflangLinks.some(link => link.lang === 'x-default');

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {author && <meta name="author" content={author} />}
      <link rel="canonical" href={canonical} />

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={OG_SITE_NAME} />
      {locale && <meta property="og:locale" content={locale} />}

      {/* Standard Open Graph article tags (publications render as og:type=article) */}
      {type === 'article' && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {type === 'article' && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {type === 'article' && author && <meta property="article:author" content={author} />}

      {/* Profile-specific OG tags (only for og:type=profile) */}
      {type === 'profile' && profileFirstName && <meta property="profile:first_name" content={profileFirstName} />}
      {type === 'profile' && profileLastName && <meta property="profile:last_name" content={profileLastName} />}

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={TWITTER_SITE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      

      {/* SEO Control Meta Tags */}
      <meta 
        name="robots" 
        content={noindex 
          ? "noindex, nofollow" 
          : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        } 
      />
      <meta name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Hreflang Links for Multilingual Support */}
      {hreflangLinks.length > 0 && hreflangLinks.map((link) => (
        <link key={link.lang} rel="alternate" hrefLang={link.lang} href={link.url} />
      ))}
      {/* x-default hreflang — only if not already in the array */}
      {hreflangLinks.length > 0 && !hasXDefault && (
        <link rel="alternate" hrefLang="x-default" href={canonical} />
      )}

      {/* Schema.org Structured Data */}
      {schemas.map((schemaObj, index) => (
        <script key={`schema-${index}`} type="application/ld+json">
          {JSON.stringify(schemaObj)}
        </script>
      ))}
    </Helmet>
  );
}

// Re-export schema generators for convenience
export * from '@/lib/seoSchemas';
